<template>
  <SoInfoBox
    v-if="eligibleForSmartMeterInstall"
    :loading="loading"
    title="Smart Meter Installation"
    icon="wrench"
    data-testid="smart-reading-installation-info-box"
  >
    <div class="d-flex gap-1 flex-wrap font-weight-medium align-center">
      <SoStatus
        ref="smart-meter-availability-status"
        :status="smartMeterInstallStatus"
        outlined
      />
    </div>
    <SoButton
      v-if="hasAvailableSlots"
      title="Book"
      class="mt-4 d-block"
      icon="chevron-right"
      data-testid="imbp-book"
      @click="openIMBP()"
    />
    <ImbpRegisterInterestDropdown
      v-if="isRegisteredToSmartMeterInterest"
      @open-modal="isImbpInterestModalOpen = true"
    />
    <SoButton
      v-else-if="!hasAvailableSlots"
      title="Collect Availability"
      class="mt-4 d-block"
      icon="chevron-right"
      data-testid="imbp-register-interest"
      @click="isImbpInterestModalOpen = true"
    />
    <SoLink
      text="Customer Checks"
      class="mt-4"
      @click="isCustomerChecksModalOpen = true"
    />
    <div>
      <SoLink
        v-if="hasSlotOfferingHistory"
        :text="smartMeterOfferedSlotsText"
        class="mt-4"
        data-testid="imbp-previous-slots"
        @click="isPreviouslyOfferedSlotsModalOpen = true"
      />
    </div>
    <SoLink
      v-if="canStopSlotOfferingEmails"
      text="Stop slot offering emails"
      class="mt-4"
      data-testid="stop-slot-offering-emails"
      @click="isStopSlotOfferingModalOpen = true"
    />
    <SoLink
      :href="`https://soenergy.typeform.com/to/${typeformIds.SMART_METER_UNSUBSCRIBE_EMAIL}`"
      text="Unsubscribe from campaign emails"
      icon="external-link sm"
      class="mt-4"
      data-testid="smart-meter-unsubscribe-email"
    />
  </SoInfoBox>

  <SoInfoBox
    title="Troubleshooting"
    icon="wrench"
    data-testid="troubleshooting-info-box"
  >
    Diagnose and resolve common meter faults, emergencies & connection issues.
    <br />
    <SoLink
      text="Troubleshoot metering issues"
      :href="externalRoutes.MeteringTroubleshootingArticles.path"
      icon="external-link"
      class="font-weight-medium mt-6"
    />
  </SoInfoBox>

  <SoInfoBox
    title="Internal Meter Booking Portal"
    icon="calendar"
    data-testid="internal-meter-booking-portal-info-box"
  >
    Book a smart meter install or any other meter appointment.
    <br />
    <SoLink
      text="Go to IMBP"
      icon="chevron-right"
      class="font-weight-medium mt-6"
      @click="openIMBP()"
    />
  </SoInfoBox>

  <SoInfoBox
    title="Links & Tips"
    icon="help-circle"
    data-testid="links-and-tips-info-box"
  >
    <SoLink
      text="So Acronym"
      href="https://soenergygroup.sharepoint.com/SoGoodToKnow/SitePages/Acronyms.aspx"
      icon="external-link sm"
    />
    <br />
    <SoLink
      text="An Energy Supplier's Responsibilities"
      class="mt-4"
      @click="isSuppliersResponseModalOpen = true"
    />
    <br />
    <SoLink
      text="Customer thinks their meter is faulty"
      class="mt-4"
      @click="isFaultyMeterModalOpen = true"
    />
    <template v-if="hasSmartMeter">
      <br />
      <SoLink
        text="IHD troubleshooting"
        class="mt-4"
        data-testid="ihd-troubleshooting"
        @click="isIhdTroubleshootingModalOpen = true"
      />
      <br />
      <SoLink
        text="Installation Guide Booklet"
        class="mt-4"
        @click="isInstallationGuideModalOpen = true"
      />
      <br />
      <SoLink
        text="Smart Meter Tips"
        class="mt-4"
        @click="isSmetsMeterTipsModalOpen = true"
      />
    </template>
  </SoInfoBox>

  <SoInfoBox
    title="Metering Knowledge Base"
    icon="meter"
    data-testid="metering-knowledge-base-info-box"
  >
    <SoLink
      text="Not Faulty Meter"
      class="mt-4"
      href="https://home.so.energy/series/2119682-series-metering-troubleshooting"
      icon="external-link sm"
    />
    <br />
    <SoLink
      text="Faulty Meter"
      class="mt-4"
      href="https://home.so.energy/series/2119682-series-metering-troubleshooting"
      icon="external-link sm"
    />
    <br />
    <SoLink
      text="Other Metering"
      class="mt-4"
      href="https://home.so.energy/series/2119682-series-metering-troubleshooting"
      icon="external-link sm"
    />
    <br />
    <SoLink
      text="Bookings"
      class="mt-4"
      href="https://home.so.energy/series/2119682-series-metering-troubleshooting"
      icon="external-link sm"
    />
    <br />
    <SoLink
      text="Emergencies"
      class="mt-4"
      href="https://home.so.energy/posts/2119702-emergencies-metering-step-by-step"
      icon="external-link sm"
    />
    <br />
    <SoLink
      text="Submitting a Meter Read"
      class="mt-4"
      href="https://home.so.energy/posts/2119655-how-to-submit-meter-readings-process-explained"
      icon="external-link sm"
    />
    <br />
    <SoLink
      text="Disputed Reads"
      class="mt-4"
      href="https://home.so.energy/series/2119846-series-disputed-reads"
      icon="external-link sm"
    />
  </SoInfoBox>

  <CustomerChecksModal v-model="isCustomerChecksModalOpen" />
  <PreviouslyOfferedSlotsModal
    ref="previous-slots-modal"
    v-model="isPreviouslyOfferedSlotsModalOpen"
    :slots="smartMeterOfferedSlotsHistory.data ?? []"
  />
  <SuppliersResponsibilitiesModal v-model="isSuppliersResponseModalOpen" />
  <FaultyMeterModal v-model="isFaultyMeterModalOpen" />
  <InstallationGuideModal v-model="isInstallationGuideModalOpen" />
  <SmetsMeterTipsModal v-model="isSmetsMeterTipsModalOpen" />
  <IHDTroubleshootingModal v-model="isIhdTroubleshootingModalOpen" />
  <ImbpRegisterInterestModal v-model="isImbpInterestModalOpen" />
  <StopSlotOfferingEmailsModal v-model="isStopSlotOfferingModalOpen" />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { MeterpointService } from "@/services/MeterpointService"
  import { typeformIds } from "@/constants/typeformIds"
  import SoInfoBox from "@/components/SoInfoBox.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoLink from "@/components/SoLink.vue"
  import CustomerChecksModal from "../modals/CustomerChecksModal.vue"
  import SuppliersResponsibilitiesModal from "../modals/SuppliersResponsibilitiesModal.vue"
  import FaultyMeterModal from "../modals/FaultyMeterModal.vue"
  import InstallationGuideModal from "../modals/InstallationGuideModal.vue"
  import IHDTroubleshootingModal from "../modals/IHDTroubleshootingModal.vue"
  import SmetsMeterTipsModal from "../modals/SmetsMeterTipsModal.vue"
  import { RouteNames } from "@/types/router"
  import { Status } from "@/types/Status"
  import { SmartInterestReason } from "@/models/smartMeterInterest/smartMeterInterest.types"
  import { externalRoutes } from "@/constants/routes/externalRoutes"
  import SoButton from "@/components/formControls/SoButton.vue"
  import ImbpRegisterInterestModal from "../../imbp/components/ImbpRegisterInterestModal.vue"
  import ImbpRegisterInterestDropdown from "../../imbp/components/ImbpRegisterInterestDropdown.vue"
  import PreviouslyOfferedSlotsModal from "../modals/PreviouslyOfferedSlotsModal.vue"
  import StopSlotOfferingEmailsModal from "../modals/StopSlotOfferingEmailsModal.vue"

  export default defineComponent({
    components: {
      SoButton,
      SoInfoBox,
      SoStatus,
      SoLink,
      CustomerChecksModal,
      PreviouslyOfferedSlotsModal,
      SuppliersResponsibilitiesModal,
      FaultyMeterModal,
      InstallationGuideModal,
      SmetsMeterTipsModal,
      IHDTroubleshootingModal,
      ImbpRegisterInterestModal,
      ImbpRegisterInterestDropdown,
      StopSlotOfferingEmailsModal,
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        MeterpointService,
        typeformIds,
        isCustomerChecksModalOpen: false,
        isPreviouslyOfferedSlotsModalOpen: false,
        isSuppliersResponseModalOpen: false,
        isFaultyMeterModalOpen: false,
        isInstallationGuideModalOpen: false,
        isSmetsMeterTipsModalOpen: false,
        isIhdTroubleshootingModalOpen: false,
        isImbpInterestModalOpen: false,
        isStopSlotOfferingModalOpen: false,
        SmartInterestReason,
        externalRoutes,
      }
    },
    computed: {
      hasSmartMeter() {
        return this.checkMeterpointTypes(true)
      },
      eligibleForSmartMeterInstall() {
        return this.current.smartMeterEligibility.data.isEligible
      },
      isSmbpInfoBoxLoading() {
        return this.current.imbpAvailability.isFetching
      },
      hasAvailableSlots() {
        return this.current.imbpAvailability.data.hasAvailableSlots
      },
      isRegisteredToSmartMeterInterest() {
        return !!this.current.imbpSmartMeterInterest.data?.registered
      },
      canStopSlotOfferingEmails() {
        return this.isRegisteredToSmartMeterInterest
      },
      smartMeterInstallStatus(): Status {
        return this.hasAvailableSlots
          ? {
              text: `${this.current.imbpAvailability.data.totalSlots} slots available`,
              type: "normal",
            }
          : {
              text: "No slots available",
              type: "critical",
            }
      },
      isImbpSmartMeterInterestLoading() {
        return this.current.imbpSmartMeterInterest.isFetching
      },
      smartMeterEligibilityLoading() {
        return this.current.smartMeterEligibility.isFetching
      },
      loading() {
        return (
          this.isImbpSmartMeterInterestLoading &&
          this.isSmbpInfoBoxLoading &&
          this.smartMeterEligibilityLoading
        )
      },
      smartMeterOfferedSlotsHistory() {
        return this.current.imbpPreviouslyOfferedSlots
      },
      hasSlotOfferingHistory() {
        return (this.smartMeterOfferedSlotsHistory?.data?.length ?? 0) > 0
      },
      smartMeterOfferedSlotsText() {
        const slotsOffered = this.smartMeterOfferedSlotsHistory.data || []
        const count = slotsOffered.length

        return `${count} slot${count === 1 ? "" : "s"} previously offered`
      },
    },
    methods: {
      checkMeterpointTypes(checkForSmartMeter: boolean) {
        return MeterpointService.getForAccount(
          this.current.accountNumber,
        ).data.some(
          (meterpoint) => meterpoint.hasSmartMeter === checkForSmartMeter,
        )
      },
      openIMBP() {
        this.$router.push({ name: RouteNames.CustomerImbp })
      },
    },
  })
</script>

<style scoped>
  .info-box-link {
    color: rgb(var(--v-theme-primary));
    text-decoration: none;
    display: flex;
    align-items: center;
  }
</style>
